// BranchPage.tsx - İyileştirilmiş versiyon
import React, { FC, useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { Form, Formik, FormikProps } from 'formik'
import { ConfirmModal } from '../../../../shared/components/ConfirmModal'
import {
  IXmlTariffFilterModel,
  IXmlTariffModel,
  TARIFF_SUB_TYPE_OPTIONS,
  initialXmlTariff,
  validationXmlTariff,
} from './XmlTariffModel'
import { ToastContainer } from 'react-toastify' 
import { showErrorToast, showSuccessToast } from '../../../../shared/components/ToastUtils'
import { createXmlTariff, deleteXmlTariff, getXmlTariffById, loadXmlTariffs, updateXmlTariff } from './Api'
import FormTabHeader, { TabOption } from '../../../../shared/components/FormTabHeader'
import TextInput from '../../../../shared/components/TextInput'
import SelectInput from '../../../../shared/components/SelectInput'
import { XmlTariffTable } from './XmlTariffTable'
import { APPROVAL_STATUS_OPTIONS } from '../xml-tariff/XmlTariffModel'
import { TYPE_OPTIONS } from '../xml-tariff/XmlTariffModel'
import { STATUS_OPTIONS } from '../xml-tariff/XmlTariffModel'
import {
  reservationSources,
  branches,
  locations,
} from '../../../../shared/models/SharedApiLookups'
import { useHistory, useLocation } from 'react-router-dom'
import DateInput from '../../../../shared/components/DateInput'
import MultiSelectCombobox from '../../../../shared/components/MultiSelectCombobox'
import { send } from 'process'

/** API'den gelen nesne dizisini id dizisine çevirir */
function toIdArray(arr: unknown): string[] {
  if (!Array.isArray(arr)) return []
  return arr.map((item: any) => (typeof item === 'object' && item !== null ? String(item.id) : String(item))).filter(Boolean)
}

const XmlTariffPage: FC = () => {
  const [loading, setLoading] = useState(false)
  const [loadingApproval, setLoadingApproval] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [xmlTariffs, setXmlTariffs] = useState<IXmlTariffModel[]>([])

  const [tab, setTab] = useState<string | 'form'>('form')
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [sortBy, setSortBy] = useState<string>('')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [error, setError] = useState<string | null>(null)
  const [listFilterOpen, setListFilterOpen] = useState(true)
  const [isDayDisabled, setIsDayDisabled] = useState(false)
  const history = useHistory()

  const [reservationSourceOptions, setReservationSourceOptions] = useState<
    { value: string; label: string }[]
  >([])
  const [branchOptions, setBranchOptions] = useState<{ value: string; label: string }[]>([])
  const [locationOptions, setLocationOptions] = useState<{ value: string; label: string }[]>([])

  const tabOptions: TabOption<'form' | 'detail' | 'list'>[] = [
    { key: 'form', title: 'Xml Tarife Tanımlama', icon: 'bi bi-geo-alt' },
    { key: 'list', title: 'Xml Tarife Listesi', icon: 'bi bi-list' },
  ]
  const location = useLocation()

  const tariffIdFromUrl = useMemo(() => {
    const q = new URLSearchParams(location.search)
    return q.get('tariffId')?.trim() || ''
  }, [location.search])
  const [selectedId, setSelectedId] = useState<string | null>(tariffIdFromUrl || null)
  const formikRef = useRef<FormikProps<IXmlTariffModel>>(null)
  const [initialFormValues, setInitialFormValues] = useState<IXmlTariffModel>(initialXmlTariff)

  // URL'den tariffId geldiğinde tarife verisini yükle ve formu doldur
  useEffect(() => {
    if (!tariffIdFromUrl) return
    let cancelled = false
    getXmlTariffById(tariffIdFromUrl)
      .then((res) => {
        if (cancelled) return
        if (res.data.hasError || !res.data.payload) {
          showErrorToast(res.data.message || 'Tarife bilgisi alınamadı')
          return
        }
        const tariff = res.data.payload
        setSelectedId(tariff.id)
        setTab('form')
        setInitialFormValues({
          ...tariff,
          reservationSource: toIdArray(tariff.reservationSource) as any,
          actionBranch: toIdArray(tariff.actionBranch) as any,
          specialLocation: toIdArray(tariff.specialLocation) as any,
        })
        if(tariff.tariffSubType !== 'GENERAL' && tariff.tariffSubType !== 'SEGMENT') {
          setIsDayDisabled(true)
        } else {
          setIsDayDisabled(false)
        }

      })
      .catch(() => {
        if (!cancelled) {
          showErrorToast('Tarife bilgisi yüklenemedi')
        }
      })
    return () => { cancelled = true }
  }, [tariffIdFromUrl])

  const fetchXmlTariffs = useCallback(
    async (page: number = 0, sort?: string, order?: 'asc' | 'desc', filters?: IXmlTariffFilterModel) => {
      setLoading(true)
      setError(null)

      try {
        const response = await loadXmlTariffs(page, 5, sort, order, filters)
        if (response.data.hasError) {
          throw new Error(response.data.message || 'Failed to fetch xml-tariffs')
        }
        setXmlTariffs(response.data.payload.content)
        setTotalPages(response.data.payload.totalPages)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error occurred')
      } finally {
        setLoading(false)
      }
    },
    []
  )

  // İlk yükleme
  useEffect(() => {
    reservationSources().then(setReservationSourceOptions)
    branches().then((brn) => setBranchOptions([{ value: '0', label: 'Hepsi' }, ...brn]))
    locations().then((loc) => setLocationOptions([{ value: '0', label: 'Hepsi' }, ...loc]))

    if (tab === 'list') {
      fetchXmlTariffs(0)
    }
  }, [tab, fetchXmlTariffs])

  const handleSave = async (values: IXmlTariffModel, resetForm: () => void) => {
    setLoading(true)
    try {
      if (selectedId) {
        await updateXmlTariff({ ...values, id: selectedId })
        showSuccessToast('Xml Tarifesi başarıyla güncellendi')
      } else {
        await createXmlTariff(values)
        showSuccessToast('Xml Tarifesi başarıyla oluşturuldu')
      }
      setCurrentPage(0)
      await fetchXmlTariffs(0)
      resetForm()
      setSelectedId(null)
      setTab('list')
    } catch (err) {
      showErrorToast('Bir hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = useCallback(
    (xmlTariff: IXmlTariffModel, formik: FormikProps<IXmlTariffModel>) => {
      formik.setValues({
        ...xmlTariff,
        reservationSource: toIdArray(xmlTariff.reservationSource) as any,
        actionBranch: toIdArray(xmlTariff.actionBranch) as any,
        specialLocation: toIdArray(xmlTariff.specialLocation) as any,
      })
      setSelectedId(xmlTariff.id)
      setTab('form')
      if(xmlTariff.tariffSubType !== 'GENERAL' && xmlTariff.tariffSubType !== 'SEGMENT') {
        setIsDayDisabled(true)
      } else {
        setIsDayDisabled(false)
      }
    },
    []
  )

  const handleDeleteClick = useCallback((xmlTariff: IXmlTariffModel) => {
    setSelectedId(xmlTariff.id)
    setShowModal(true)
  }, [])

  const handleDelete = async () => {
    if (selectedId) {
      setLoading(true)
      try {
        await deleteXmlTariff(selectedId)
        showSuccessToast('Xml Tarifesi başarıyla silindi')
        await fetchXmlTariffs(currentPage)
      } catch (err) {
        console.error(err)
        showErrorToast('Xml Tarifesi silinirken bir hata oluştu')
      } finally {
        setLoading(false)
        setShowModal(false)
        setSelectedId(null)
      }
    }
  }

  const handlePageChange = useCallback(
    (newPage: number, formik: FormikProps<IXmlTariffModel>) => {
      if (newPage >= 0 && newPage < totalPages) {
        setCurrentPage(newPage)
        fetchXmlTariffs(newPage, sortBy, sortOrder, formik.values.filters)
      }
    },
    [totalPages, sortBy, sortOrder, fetchXmlTariffs]
  )

  const handleSort = useCallback(
    (column: string, order: 'asc' | 'desc', formik: FormikProps<IXmlTariffModel>) => {
      setSortBy(column)
      setSortOrder(order)
      fetchXmlTariffs(currentPage, column, order, formik.values.filters)
    },
    [currentPage, fetchXmlTariffs]
  )

  return (
    <>
      <Formik
        innerRef={formikRef}
        initialValues={initialFormValues}
        validationSchema={validationXmlTariff}
        enableReinitialize
        onSubmit={(values, { resetForm }) => handleSave(values, resetForm)}
      >
        {(formik) => {
          const handleReset = () => {
            setInitialFormValues(initialXmlTariff)
            formik.resetForm()
            setSelectedId(null)
            setTab('form')
          }

          const sendApprovalRequest = async () => {
            if (!selectedId && !formik.values.id) {
              showErrorToast('Lütfen önce bir tarife seçin veya oluşturun')
              return
            }

            const tariffId = selectedId || formik.values.id
            setLoadingApproval(true)
            try {
              await updateXmlTariff({
                ...formik.values,
                id: tariffId,
                approvalStatus: 'PENDING' 
              })
              showSuccessToast('Onay isteği başarıyla gönderildi')
              await fetchXmlTariffs(currentPage)
              handleReset()
              setSelectedId(null)
              setTab('list')
            } catch (err) {
              showErrorToast('Onay isteği gönderilirken bir hata oluştu')
              console.error(err)
            } finally {
              setLoadingApproval(false)
            }
          }

          return (
            <>
              <FormTabHeader
                formId='xmlTariffForm'
                formikSubmit={formik.handleSubmit}
                formikReset={handleReset}
                loading={loading}
                tab={tab}
                setTab={setTab}
                setSelectedId={setSelectedId}
                tabs={tabOptions}
                onExportExcel={() => console.log('Excel export')}
              />

              {/* Form ve Detail Tabları */}
              {tab === 'form' && (
                <div className='card mb-5'>
                  <div className='card-header'>
                    <div className='card-title'>
                      <div className='d-flex align-items-center gap-2 my-1'>
                        <h3 className='fw-bold m-0'>Tarife Giriş</h3>
                      </div>
                    </div>
                    <div className='card-toolbar'>
                      <button
                        type='button'
                        className='btn btn-success me-2'
                        disabled={!selectedId}
                        hidden={formik.values.approvalStatus === 'APPROVED' || formik.values.approvalStatus === 'REJECTED' || formik.values.approvalStatus === 'PENDING'}
                        onClick={() => sendApprovalRequest()}
                      >
                        <i className='bi bi-check-circle fs-5'></i>
                        {loadingApproval ? 'Onaya Gönderiliyor...' : 'Onaya Gönder'}
                      </button>
                      <button
                        type='button'
                        className='btn btn-primary'
                        disabled={!selectedId || formik.values.approvalStatus === 'PENDING'}
                        onClick={() => {
                          const id = selectedId || formik.values.id
                          history.push(
                            id
                              ? `/definition/xml-tariff-detail?tariffId=${encodeURIComponent(id)}`
                              : '/definition/xml-tariff-detail'
                          )
                        }}
                      >
                        <i className='bi bi-currency-dollar fs-5'></i>
                        Fiyat Listesine Git
                      </button>
                    </div>
                  </div>
                  <div className='card-body border-top pt-6'>
                    <Form id='xmlTariffForm' className='form card-body' noValidate>
                      {tab === 'form' && (
                        <>
                          <div className='row mb-6'>
                            <TextInput
                              name='rateName' //1
                              label='Tarife Adı'
                              wrapperClassName='col-md-6'
                            />
                            <DateInput
                              name='actionDate' // 11
                              label='İşlem Tarihi'
                              type='date'
                              wrapperClassName='col-md-3'
                            />
                            <SelectInput
                              name='tariffSubType'
                              label='Tarife Alt Türü'
                              wrapperClassName='col-md-3'
                              options={TARIFF_SUB_TYPE_OPTIONS}
                              onChange={(value) => {
                                setIsDayDisabled(!(typeof value === 'string' && (value === 'GENERAL' || value === 'SEGMENT'))) 
                                }
                              }
                            />
                          </div>                          
                          <div className='row mb-6'>
                            <TextInput
                              name='dayOne' //2
                              type='number'
                              label='Gün 1'
                              readOnly={isDayDisabled}
                              wrapperClassName='col-md-6'
                            />
                            <DateInput
                              name='startDate' // 12
                              label='Başlama Tarihi'
                              type='date'
                              wrapperClassName='col-md-6'
                            />
                          </div>
                          <div className='row mb-6'>
                            <TextInput
                              name='dayTwo' //3
                              type='number'
                              label='Gün 2'
                              readOnly={isDayDisabled}
                              wrapperClassName='col-md-6'
                            />

                            <DateInput
                              name='endDate' // 13
                              label='Bitiş Tarihi'
                              type='date'
                              wrapperClassName='col-md-6'
                            />
                          </div>
                          <div className='row mb-6'>
                            <TextInput
                              name='dayThree' //4
                              type='number'
                              label='Gün 3'
                              readOnly={isDayDisabled}
                              wrapperClassName='col-md-6'
                            />
                            <DateInput
                              name='maxRentalDate' //14
                              type='date'
                              label='Max Kira Kapsamı'
                              wrapperClassName='col-md-6'
                            />
                          </div>
                          <div className='row mb-6'>
                            <TextInput
                              name='dayFour' //5
                              type='number'
                              label='Gün 4'
                              readOnly={isDayDisabled}
                              wrapperClassName='col-md-6'
                            />
                            <MultiSelectCombobox
                              name='reservationSource'
                              label='Rezervasyon Kaynağı'
                              wrapperClassName='col-md-6'
                              options={reservationSourceOptions}
                            />
                          </div>
                          <div className='row mb-6'>
                            <TextInput
                              name='dayFive' //6
                              type='number'
                              label='Gün 5'
                              readOnly={isDayDisabled}
                              wrapperClassName='col-md-6'
                            />
                            <SelectInput
                              name='status' //16
                              label='Durum'
                              wrapperClassName='col-md-6'
                              options={STATUS_OPTIONS}
                              disabled={formik.values.approvalStatus !== 'APPROVED'}
                            />
                          </div>
                          <div className='row mb-6'>
                            <TextInput
                              name='daySix' //7
                              type='number'
                              label='Gün 6'
                              wrapperClassName='col-md-6'
                              readOnly={isDayDisabled}
                            />
                            <SelectInput
                              name='tariffType' // 17
                              label='Türü'
                              wrapperClassName='col-md-6'
                              options={TYPE_OPTIONS}
                            />
                          </div>
                          <div className='row mb-6'>
                            <TextInput
                              name='daySeven' // 8
                              type='number'
                              label='Gün 7'
                              wrapperClassName='col-md-6'
                              readOnly={isDayDisabled}
                            />
                            <TextInput
                              name='comparisonSystemDiscountRate' // 18
                              type='number'
                              label='Karşılaştırma Sistemi İndirim Oranı %'
                              wrapperClassName='col-md-6'
                            />
                          </div>

                          <div className='row mb-6'>
                            <DateInput
                              name='approvalDate'
                              type='datetime-local'
                              label='Onaylanma Tarihi'
                              wrapperClassName='col-md-3'
                              readOnly={true}
                            />
                            <TextInput
                              name='approvalBy' //10
                              label='Onaylayan'
                              wrapperClassName='col-md-3'
                              readOnly={true}
                            />

                            <MultiSelectCombobox
                              name='actionBranch' // 19
                              label='İşlem Şube'
                              wrapperClassName='col-md-6'
                              options={branchOptions}
                            />
                          </div>

                          <div className='row mb-6'>
                            <SelectInput
                              name='approvalStatus' // 20
                              label='Onay Durumu'
                              wrapperClassName='col-md-6'
                              options={APPROVAL_STATUS_OPTIONS}
                              disabled={true}
                            />
                            <MultiSelectCombobox
                              name='specialLocation' // 23
                              label='Özel Lokasyon'
                              wrapperClassName='col-md-6'
                              options={locationOptions}
                            />
                          </div>

                          <div className='row mb-6'>
                            <DateInput
                              name='createdDate'
                              label='Oluşturulma Tarihi'
                              type='datetime-local'
                              wrapperClassName='col-md-3'
                              readOnly={true}
                            />
                            <TextInput
                              name='createdByUsername' //21
                              label='Oluşturan Kullanıcı'
                              wrapperClassName='col-md-3'
                              readOnly={true}
                            />
                          </div>
                          <div className='row mb-6'>
                            <DateInput
                              name='updatedDate'
                              label='Son Değişiklik'
                              type='datetime-local'
                              wrapperClassName='col-md-3'
                              readOnly={true}
                            />
                            <TextInput
                              name='updatedByUsername' //22
                              label='Güncelleyen Kullanıcı'
                              wrapperClassName='col-md-3'
                              readOnly={true}
                            />
                          </div>


                        </>
                      )}
                    </Form>
                  </div>
                </div>
              )}

              {/* Liste Tabı */}
              {tab === 'list' && (
                <div className='card'>
                  <div className='card-header border-0 pt-6'>
                    <div className='card-title'>
                      <div className='d-flex align-items-center gap-2 my-1'>
                        <i className='bi bi-funnel fs-1 text-primary'></i>
                        <h3 className='fw-bold m-0'>Tarife Filtreleri</h3>
                      </div>
                    </div>
                    <div className='card-toolbar'>
                      <button
                        type='button'
                        className='btn btn-sm btn-light-primary'
                        onClick={() => setListFilterOpen((prev) => !prev)}
                      >
                        <i className={`ki-duotone ki-${listFilterOpen ? 'up' : 'down'} fs-2`}>
                          <span className='path1'></span>
                          <span className='path2'></span>
                        </i>
                        {listFilterOpen ? 'Gizle' : 'Göster'}
                      </button>
                    </div>
                  </div>

                  <div className={`collapse ${listFilterOpen ? 'show' : ''}`}>
                    <div className='card-body border-top pt-6'>
                      <div className='row g-6 mb-6 align-items-end'>
                        <div className='col-md-3'>
                          <MultiSelectCombobox
                            name='filters.actionBranchSearch'
                            label='Şube'
                            placeholder='Şube Seçiniz'
                            options={branchOptions}
                          />
                        </div>
                        <div className='col-md-3'>
                          <MultiSelectCombobox
                            name='filters.reservationSourceSearch'
                            label='Rezervasyon Kaynağı'
                            placeholder='Rez. Kaynağı Seçiniz'
                            options={reservationSourceOptions}
                          />
                        </div>
                        <div className='col-md-3'>
                          <SelectInput
                            name='filters.statusSearch'
                            label='Durum'
                            firstItemLabel='Hepsi'
                            options={STATUS_OPTIONS}
                          />
                        </div>
                        <div className='col-md-3'>
                          <SelectInput
                            name='filters.approvalStatusSearch'
                            label='Onay Durumu'
                            firstItemLabel='Hepsi'
                            options={APPROVAL_STATUS_OPTIONS}
                          />
                        </div>
                      </div>
                      <div className='row g-6 mb-0 align-items-end'>
                        <div className='col-md-3'>
                          <SelectInput
                            name='filters.dateType'
                            label='Tarih Türü'
                            firstItemLabel='Tarih Türü Seçiniz'
                            options={[ 
                              { value: 'start', label: 'Başlangıç Tarihi' },
                              { value: 'end', label: 'Bitiş Tarihi' },
                            ]}
                          />
                        </div>
                        <div className='col-md-3'>
                          <TextInput name='filters.startDateSearch' type='date' label='Başlangıç Tarihi' />
                        </div>
                        <div className='col-md-3'>
                          <TextInput name='filters.endDateSearch' type='date' label='Bitiş Tarihi' />
                        </div>
                        <div className='col-md-3'>
                          <button
                            type='button'
                            className='btn btn-info d-flex align-items-center justify-content-center gap-2 w-100'
                            onClick={() => {
                              const filters: IXmlTariffFilterModel = { 
                                actionBranchSearch: formik.values.filters.actionBranchSearch,
                                reservationSourceSearch: formik.values.filters.reservationSourceSearch,
                                statusSearch: formik.values.filters.statusSearch,
                                approvalStatusSearch: formik.values.filters.approvalStatusSearch,
                                dateType: formik.values.filters.dateType,
                                startDateSearch: formik.values.filters.startDateSearch,
                                endDateSearch: formik.values.filters.endDateSearch,
                              }
                              console.log('Filtre gönderildi:', filters)
                              setCurrentPage(0)
                              fetchXmlTariffs(0, sortBy, sortOrder, filters);
                            }}
                          >
                            <i className='bi bi-funnel'></i> Filtrele
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className='card-body pt-0'>
                    <XmlTariffTable
                      data={xmlTariffs}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={(newPage) => handlePageChange(newPage, formik)}
                      onEdit={(xmlTariff) => handleEdit(xmlTariff, formik)}
                      onDelete={handleDeleteClick}
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={(column, order) => handleSort(column, order, formik)}
                      loading={loading}
                    />
                  </div>
                </div>
              )}
            </>
          )
        }}
      </Formik>

      <ConfirmModal
        show={showModal}
        header='Silme Onayı'
        text='Bu Xml Tarifeyi silmek istediğinize emin misiniz?'
        handleClose={() => {
          setShowModal(false)
          setSelectedId(null)
        }}
        applyOnClick={handleDelete}
      />

      <ToastContainer
        position='top-right'
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  )
}

export { XmlTariffPage }
